import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { lookupCompanies } from '../services/authService';
import useWindowWidth from '../hooks/useWindowWidth';
import api from '../services/api';
import { getPublicPlans, registerAccount } from '../services/publicService';
import {
  isWebAuthnSupported,
  getStoredCredential,
  getCredential,
} from '../services/webAuthnService';
import './LoginPage.css';

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v ?? 0);

const CYCLE_LABELS = {
  monthly: 'mês',
  quarterly: 'trimestre',
  annual: 'ano',
  custom: 'período',
};

function maskCpfCnpj(value) {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  if (digits.length <= 11) {
    return digits
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  return digits
    .replace(/(\d{2})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1/$2')
    .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
}

// ── Plan Card (login page) ────────────────────────────────────────────────────
const FIXED_LIMITS = [
  { key: 'limit_banks',      label: 'Bancos' },
  { key: 'limit_cards',      label: 'Cartões' },
  { key: 'limit_categories', label: 'Categorias' },
  { key: 'limit_providers',  label: 'Fornecedores' },
  { key: 'limit_customers',  label: 'Clientes' },
];

function PlanCard({ plan, onContract }) {
  const inf = (v) => (v === null || v === undefined ? '∞' : v);
  return (
    <div className="lp-plan-card">
      <div className="lp-plan-trial-badge">30 dias grátis</div>
      <div className="lp-plan-name">{plan.name}</div>
      <div className="lp-plan-price">
        <span className="lp-plan-price-value">{fmt(plan.price)}</span>
        <span className="lp-plan-price-cycle">/{CYCLE_LABELS[plan.billing_cycle] || 'mês'}</span>
      </div>
      {plan.description && <p className="lp-plan-desc">{plan.description}</p>}

      <div className="lp-plan-limits">
        {FIXED_LIMITS.map(({ key, label }) => (
          <div key={key} className="lp-plan-limit-item">
            <span className="lp-plan-limit-val">{inf(plan[key])}</span>
            <span className="lp-plan-limit-lbl">{label}</span>
          </div>
        ))}
        <div className="lp-plan-limit-item">
          <span className="lp-plan-limit-val">{inf(plan.max_users)}</span>
          <span className="lp-plan-limit-lbl">Usuários</span>
        </div>
      </div>

      <div className="lp-plan-monthly-limits">
        {[
          { key: 'limit_monthly_revenues',  label: 'Recebimentos/mês' },
          { key: 'limit_monthly_expenses',  label: 'Pagamentos/mês' },
          { key: 'limit_monthly_transfers', label: 'Transferências/mês' },
        ].map(({ key, label }) => (
          <div key={key} className="lp-plan-monthly-row">
            <span className="lp-plan-monthly-label">{label}</span>
            <span className="lp-plan-monthly-val">
              {plan[key] === null || plan[key] === undefined ? 'Ilimitado' : plan[key]}
            </span>
          </div>
        ))}
      </div>

      {plan.features && plan.features.length > 0 && (
        <ul className="lp-plan-features">
          {plan.features.map((f, i) => <li key={i}>✓ {f}</li>)}
        </ul>
      )}

      <button className="lp-plan-btn" onClick={() => onContract(plan)}>
        Contratar Agora
      </button>
    </div>
  );
}

// ── Signup Modal ──────────────────────────────────────────────────────────────
function SignupModal({ plan, onClose, onSuccess }) {
  const [form, setForm] = useState({
    company_name: '',
    name: '',
    email: '',
    document_number: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: name === 'document_number' ? maskCpfCnpj(value) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await registerAccount({ ...form, plan_id: plan.id });
      onSuccess();
    } catch (err) {
      const errors = err.response?.data?.errors;
      setError(
        errors
          ? Object.values(errors).flat().join(' ')
          : (err.response?.data?.message || 'Erro ao realizar cadastro.')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lp-modal-overlay" onClick={onClose}>
      <div className="lp-modal" onClick={e => e.stopPropagation()}>
        <div className="lp-modal-header">
          <h3>Criar Conta</h3>
          <button className="lp-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="lp-modal-plan-badge">
          Plano: <strong>{plan.name}</strong> &mdash; {fmt(plan.price)}/{CYCLE_LABELS[plan.billing_cycle] || 'mês'}
          <span className="lp-modal-trial-note"> · 30 dias grátis</span>
        </div>

        <form onSubmit={handleSubmit} className="lp-modal-form">
          {error && <div className="lp-modal-error">{error}</div>}

          <div className="lp-field">
            <label className="lp-label">Nome da Empresa *</label>
            <div className="lp-input-wrap">
              <span className="lp-input-icon">&#127970;</span>
              <input
                className="lp-input"
                name="company_name"
                value={form.company_name}
                onChange={handleChange}
                placeholder="Nome da sua empresa"
                required
              />
            </div>
          </div>

          <div className="lp-field">
            <label className="lp-label">Seu Nome *</label>
            <div className="lp-input-wrap">
              <span className="lp-input-icon">&#128100;</span>
              <input
                className="lp-input"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Nome completo"
                required
              />
            </div>
          </div>

          <div className="lp-field">
            <label className="lp-label">CNPJ ou CPF *</label>
            <div className="lp-input-wrap">
              <span className="lp-input-icon">&#128196;</span>
              <input
                className="lp-input"
                name="document_number"
                value={form.document_number}
                onChange={handleChange}
                placeholder="000.000.000-00 ou 00.000.000/0000-00"
                required
              />
            </div>
          </div>

          <div className="lp-field">
            <label className="lp-label">E-mail *</label>
            <div className="lp-input-wrap">
              <span className="lp-input-icon">&#9993;</span>
              <input
                className="lp-input"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="seu@email.com"
                required
              />
            </div>
          </div>

          <div className="lp-field">
            <label className="lp-label">Telefone</label>
            <div className="lp-input-wrap">
              <span className="lp-input-icon">&#128222;</span>
              <input
                className="lp-input"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="(00) 00000-0000"
              />
            </div>
          </div>

          <button type="submit" className="lp-submit" disabled={loading}>
            {loading ? 'Enviando...' : 'Criar Minha Conta'}
          </button>

          <p className="lp-modal-terms">
            Ao criar sua conta você concorda com os termos de uso. Nenhum cartão de crédito necessário durante o período de teste.
          </p>
        </form>
      </div>
    </div>
  );
}

// ── Plans Section ─────────────────────────────────────────────────────────────
function PlansSection({ onContract }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getPublicPlans()
      .then(setPlans)
      .catch(() => setError('Não foi possível carregar os planos.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="lp-plans-loading">Carregando planos...</div>;
  }

  if (error) {
    return <div className="lp-plans-error">{error}</div>;
  }

  if (plans.length === 0) {
    return (
      <div className="lp-plans-empty">
        Nenhum plano disponível no momento. Entre em contato com o suporte.
      </div>
    );
  }

  return (
    <div className="lp-plans-wrap">
      <div className="lp-plans-header">
        <h3>Escolha seu plano</h3>
        <p>30 dias de teste grátis. Sem cartão de crédito.</p>
      </div>
      <div className="lp-plans-grid">
        {plans.map(plan => (
          <PlanCard key={plan.id} plan={plan} onContract={onContract} />
        ))}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
const LoginPage = () => {
  const [step, setStep] = useState('email'); // 'email' | 'company' | 'password'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [foundCompanies, setFoundCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [storedCred, setStoredCred] = useState(null);
  const [tab, setTab] = useState('login'); // 'login' | 'plans'
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [signupSuccess, setSignupSuccess] = useState(false);

  const { login } = useAuth();
  const width = useWindowWidth();
  const isMobile = width < 768;

  useEffect(() => {
    if (isWebAuthnSupported()) {
      setStoredCred(getStoredCredential());
    }
  }, []);

  const handleBackToEmail = useCallback(() => {
    setStep('email');
    setPassword('');
    setError('');
    setSelectedCompany(null);
    setFoundCompanies([]);
  }, []);

  const handleEmailContinue = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLookupLoading(true);
    setError('');
    try {
      const companies = await lookupCompanies(email);
      setFoundCompanies(companies);
      if (companies.length > 1) {
        setStep('company');
      } else {
        setSelectedCompany(companies[0] ?? null);
        setStep('password');
      }
    } catch {
      // On network error, proceed to password — backend handles auth
      setSelectedCompany(null);
      setStep('password');
    } finally {
      setLookupLoading(false);
    }
  };

  const handleCompanySelect = (company) => {
    setSelectedCompany(company);
    setStep('password');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await login(selectedCompany?.id ?? null, email, password);
      // Edge case: lookup failed and backend still requires company selection
      if (result?.companies) {
        setFoundCompanies(result.companies);
        setStep('company');
        return;
      }
      window.location.href = '/dashboard';
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBiometricLogin = async () => {
    if (!storedCred) return;
    setBiometricLoading(true);
    try {
      const optionsRes = await api.post('/webauthn/login/options', {
        email: storedCred.email,
        company_id: storedCred.companyId ? parseInt(storedCred.companyId, 10) : null,
      });

      const { options, challenge_token } = optionsRes.data;
      const assertion = await getCredential(options);

      const loginRes = await api.post('/webauthn/login', {
        challenge_token,
        assertion,
      });

      const { user } = loginRes.data;
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('companyId', storedCred.companyId || '');
      window.location.href = '/dashboard';
    } catch (err) {
      if (err.name === 'NotAllowedError') return;
      setError(
        err.response?.data?.message ||
          'Falha na autenticação biométrica. Use email e senha.'
      );
    } finally {
      setBiometricLoading(false);
    }
  };

  const handleContract = (plan) => {
    setSelectedPlan(plan);
    setSignupSuccess(false);
  };

  const handleSignupSuccess = () => {
    setSelectedPlan(null);
    setSignupSuccess(true);
    setTab('login');
  };

  // ── Biometric section (only on email step) ──────────────────────────────────
  const biometricSection = step === 'email' && storedCred && isWebAuthnSupported() && (
    <div className="lp-biometric-wrap">
      <button
        type="button"
        className="lp-biometric-btn"
        onClick={handleBiometricLogin}
        disabled={biometricLoading}
      >
        <span className="lp-biometric-icon">&#128274;</span>
        {biometricLoading ? 'Verificando...' : 'Entrar com biometria'}
      </button>
      <div className="lp-biometric-hint">{storedCred.email}</div>
      <div className="lp-biometric-divider"><span>ou entre com senha</span></div>
    </div>
  );

  // ── Tab bar ─────────────────────────────────────────────────────────────────
  const tabBar = (
    <div className="lp-tabs">
      <button
        className={`lp-tab${tab === 'login' ? ' lp-tab--active' : ''}`}
        onClick={() => setTab('login')}
      >
        Entrar
      </button>
      <button
        className={`lp-tab${tab === 'plans' ? ' lp-tab--active' : ''}`}
        onClick={() => setTab('plans')}
      >
        Ver Planos
      </button>
    </div>
  );

  // ── Login form (step-based) ─────────────────────────────────────────────────
  let loginForm;

  if (step === 'email') {
    loginForm = (
      <>
        {signupSuccess && (
          <div className="lp-signup-success">
            ✓ Cadastro realizado! Verifique seu e-mail para ativar sua conta.
          </div>
        )}
        {biometricSection}
        <form onSubmit={handleEmailContinue} autoComplete="off">
          <input type="text"     name="prevent_autofill"  style={{ display: 'none' }} readOnly tabIndex="-1" />

          <div className="lp-field">
            <label className="lp-label" htmlFor="email">Email</label>
            <div className="lp-input-wrap">
              <span className="lp-input-icon">&#9993;</span>
              <input
                id="email"
                type="email"
                className="lp-input"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                autoComplete="off"
                required
              />
            </div>
          </div>

          {error && <div className="lp-form-error">{error}</div>}

          <button type="submit" className="lp-submit" disabled={lookupLoading}>
            {lookupLoading ? 'Verificando...' : 'Continuar'}
          </button>

          <a href="/forgot-password" className="lp-forgot">
            Esqueceu a senha?
          </a>
        </form>
      </>
    );
  } else if (step === 'company') {
    loginForm = (
      <>
        <div className="lp-email-display">
          <span>&#9993;</span>
          <span className="lp-email-display-value">{email}</span>
          <button type="button" className="lp-change-link" onClick={handleBackToEmail}>
            Alterar
          </button>
        </div>

        <p className="lp-company-hint">Selecione a empresa em que deseja entrar:</p>

        <div className="lp-company-list">
          {foundCompanies.map(company => (
            <button
              key={company.id}
              type="button"
              className="lp-company-item"
              onClick={() => handleCompanySelect(company)}
            >
              <span className="lp-company-avatar">
                {company.name.charAt(0).toUpperCase()}
              </span>
              <span className="lp-company-name">{company.name}</span>
              <span className="lp-company-arrow">&#8594;</span>
            </button>
          ))}
        </div>

        <button type="button" className="lp-back-link" onClick={handleBackToEmail}>
          &#8592; Voltar
        </button>
      </>
    );
  } else {
    // step === 'password'
    loginForm = (
      <>
        <div className="lp-email-display">
          <span>&#9993;</span>
          <span className="lp-email-display-value">{email}</span>
          <button type="button" className="lp-change-link" onClick={handleBackToEmail}>
            Alterar
          </button>
        </div>

        {selectedCompany && (
          <div className="lp-selected-company" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>&#127970;</span>
              <span>{selectedCompany.name}</span>
            </div>
            {foundCompanies.length > 1 && (
              <button
                type="button"
                className="lp-change-link"
                onClick={() => { setStep('company'); setPassword(''); setError(''); }}
              >
                Trocar
              </button>
            )}
          </div>
        )}

        <form onSubmit={handleLogin} autoComplete="off">
          <input type="password" name="prevent_autofill_p" style={{ display: 'none' }} readOnly tabIndex="-1" />

          <div className="lp-field">
            <label className="lp-label" htmlFor="password">Senha</label>
            <div className="lp-input-wrap">
              <span className="lp-input-icon">&#128274;</span>
              <input
                id="password"
                type="password"
                className="lp-input"
                placeholder="••••••••"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                autoComplete="current-password"
                autoFocus
                required
              />
            </div>
          </div>

          {error && <div className="lp-form-error">{error}</div>}

          <button type="submit" className="lp-submit" disabled={loading}>
            {loading ? 'Entrando...' : 'Entrar'}
          </button>

          <a href="/forgot-password" className="lp-forgot">
            Esqueceu a senha?
          </a>
        </form>
      </>
    );
  }

  /* ── MOBILE ── */
  if (isMobile) {
    return (
      <div className="lp-root">
        <div className="lp-mobile">
          <div className="lp-mobile-hero">
            <div className="lp-mobile-logo">&#128188;</div>
            <div className="lp-mobile-app-name">Conta Blue</div>
            <div className="lp-mobile-tagline">Controle completo das suas finanças empresariais</div>
          </div>

          {tabBar}

          <div className={`lp-mobile-content${tab === 'plans' ? ' lp-mobile-content--plans' : ''}`}>
            {tab === 'login' ? (
              <div className="lp-mobile-form-inner">
                <h2>Bem-vindo!</h2>
                <p>Faça login para continuar</p>
                {loginForm}
              </div>
            ) : (
              <PlansSection onContract={handleContract} />
            )}
          </div>
        </div>

        {selectedPlan && (
          <SignupModal
            plan={selectedPlan}
            onClose={() => setSelectedPlan(null)}
            onSuccess={handleSignupSuccess}
          />
        )}
      </div>
    );
  }

  /* ── DESKTOP ── */
  return (
    <div className="lp-root">
      <div className="lp-desktop">
        <div className="lp-panel-left">
          <div className="lp-brand">
            <div className="lp-brand-logo">&#128188;</div>
            <div className="lp-brand-title">Conta<br />Blue</div>
            <div className="lp-brand-subtitle">
              Controle completo das finanças da sua empresa em um só lugar.
            </div>
            <div className="lp-brand-features">
              <div className="lp-feature-item">
                <div className="lp-feature-dot"></div>
                Gestão de despesas e receitas
              </div>
              <div className="lp-feature-item">
                <div className="lp-feature-dot"></div>
                Controle de cartões e faturas
              </div>
              <div className="lp-feature-item">
                <div className="lp-feature-dot"></div>
                Relatório de fluxo de caixa
              </div>
              <div className="lp-feature-item">
                <div className="lp-feature-dot"></div>
                Multi-empresas com acesso seguro
              </div>
            </div>
          </div>
        </div>

        <div className={`lp-panel-right${tab === 'plans' ? ' lp-panel-right--plans' : ''}`}>
          <div className={tab === 'plans' ? 'lp-plans-panel' : 'lp-form-box'}>
            {tabBar}

            {tab === 'login' ? (
              <>
                <h2>Bem-vindo de volta</h2>
                <p>Faça login para acessar sua conta</p>
                {loginForm}
              </>
            ) : (
              <PlansSection onContract={handleContract} />
            )}
          </div>
        </div>
      </div>

      {selectedPlan && (
        <SignupModal
          plan={selectedPlan}
          onClose={() => setSelectedPlan(null)}
          onSuccess={handleSignupSuccess}
        />
      )}
    </div>
  );
};

export default LoginPage;
